import {
  Box,
  Paper,
  Title,
  Text,
  Button,
  Flex,
  Stepper,
  Badge,
  FileButton,
  Alert,
} from "@mantine/core";
import { IconUpload, IconDownload, IconFileText } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { errorNotification } from "../../shared/utils/errorNotification";
import trpc, { saveToken, removeToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import { SignupFlowLayout } from "./SignupFlowLayout";
import {
  GT_SIGNUP_COMPANY_ID_KEY,
  GT_SIGNUP_META_KEY,
  GT_SIGNUP_UPLOAD_TOKEN_KEY,
  clearSignupFlowStorage,
  type SignupRegistrationMeta,
} from "./signupFlowStorage";

export default function SignupDocumentsPage() {
  const navigate = useNavigate();

  const [hydrated, setHydrated] = useState(false);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [meta, setMeta] = useState<SignupRegistrationMeta | null>(null);

  const [authorityLetterTemplateUrl, setAuthorityLetterTemplateUrl] = useState<string | undefined>(undefined);
  const [affidavitTemplateUrl, setAffidavitTemplateUrl] = useState<string | undefined>(undefined);
  const [mandatoryData, setMandatoryData] = useState<{ name: string; file: File | null; url: string }>({
    name: "Affidavit",
    file: null,
    url: "",
  });
  const [authorityLetterData, setAuthorityLetterData] = useState<{ name: string; file: File | null }>({
    name: "Authority Letter",
    file: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUploadToken(sessionStorage.getItem(GT_SIGNUP_UPLOAD_TOKEN_KEY));
    setCompanyId(sessionStorage.getItem(GT_SIGNUP_COMPANY_ID_KEY));
    const raw = sessionStorage.getItem(GT_SIGNUP_META_KEY);
    if (raw) {
      try {
        setMeta(JSON.parse(raw) as SignupRegistrationMeta);
      } catch {
        setMeta(null);
      }
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const r = await trpc.getTemplate.authorityLetter.query();
        setAuthorityLetterTemplateUrl(r.url);
      } catch {
        console.log("Authority letter template not available yet");
      }
      try {
        const r = await trpc.getTemplate.affidavit.query();
        setAffidavitTemplateUrl(r.url);
      } catch {
        console.log("Affidavit template not available yet");
      }
    };
    fetchTemplates();
  }, []);

  const hasAuthorityLetter = !!authorityLetterData.file;
  const hasAffidavit = !!mandatoryData.file;
  const hasRequiredDocument = hasAuthorityLetter || hasAffidavit;

  const handleUpdateMandatory = (field: string, value: unknown) =>
    setMandatoryData((prev) => ({ ...prev, [field]: value }));
  const handleUpdateAuthorityLetter = (field: string, value: unknown) =>
    setAuthorityLetterData((prev) => ({ ...prev, [field]: value }));

  const handleFinish = async () => {
    if (!uploadToken || !companyId || !hasRequiredDocument) return;

    setLoading(true);
    try {
      saveToken(uploadToken);

      if (mandatoryData.file) {
        const fd = new FormData();
        fd.append("file", mandatoryData.file);
        fd.append("category", "AFFIDAVIT");
        fd.append("companyId", companyId);
        fd.append("name", "Affidavit");
        await trpc.file.upload.mutate(fd as any);
      }

      if (authorityLetterData.file) {
        const fd = new FormData();
        fd.append("file", authorityLetterData.file);
        fd.append("category", "AUTHORITY_LETTER");
        fd.append("companyId", companyId);
        fd.append("name", "Authority Letter");
        await trpc.file.upload.mutate(fd as any);
      }

      removeToken();
      clearSignupFlowStorage();

      successNotification({
        title: "Registration complete",
        message: "Your documents were uploaded. You can log in to use the platform.",
      });

      const m = meta;
      navigate("/registration-success", {
        state: {
          companyName: m?.companyName ?? "—",
          location: m ? `${m.headquartersCity}, ${m.headquartersCountry}` : "—",
          email: m?.email ?? "—",
        },
      });
    } catch (err) {
      removeToken();
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!hydrated) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Text ta="center">Loading…</Text>
        </Paper>
      </SignupFlowLayout>
    );
  }

  if (!uploadToken || !companyId) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Alert color="yellow" title="Session expired or missing">
            Complete company details first, or log in if you already finished that step — you can upload documents from
            your account.
          </Alert>
          <Flex justify="center" gap="md" mt="lg">
            <Button component={Link} to="/signup">
              Start signup
            </Button>
            <Button component={Link} to="/login" variant="default">
              Log in
            </Button>
          </Flex>
        </Paper>
      </SignupFlowLayout>
    );
  }

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
        <Stepper active={2} mb="xl">
          <Stepper.Step label="Account" description="Verify email" />
          <Stepper.Step label="Company details" />
          <Stepper.Step label="Documents" />
        </Stepper>

        <Title order={4} mb={4} fw={400}>
          Documents
        </Title>
        <Text fz="sm" c="slate.6" mb="lg">
          Select the document that verifies your authority to represent the business in collaborations.
        </Text>

        <Box mb="sm">
          <Flex justify="space-between" align="flex-start" mb="xs">
            <Box>
              <Text fz="sm" fw={500}>
                Authority Letter (Company-issued document authorizing an individual to represent and act on behalf of the company)
              </Text>
              <Text fz="xs" c="slate.6">
                For Private/Public companies: download the template, sign it, and upload the signed document.
              </Text>
            </Box>
            <Badge color="red" variant="light" size="sm" radius="xl">
              MANDATORY
            </Badge>
          </Flex>
          <Paper withBorder shadow="none" p="sm" radius="md" mb="xs" style={{ borderColor: "#E5E7EB" }}>
            <Flex direction={{ base: "column", xs: "row" }} align={{ base: "flex-start", xs: "center" }} gap="sm">
              <Flex align="center" gap="sm" style={{ flex: 1 }}>
                <Box bg="gray.1" p="xs" style={{ borderRadius: "50%", display: "inline-flex", flexShrink: 0 }}>
                  <IconDownload size={16} color="var(--mantine-color-slate-6)" />
                </Box>
                <Box>
                  <Text fw={500} fz="sm">
                    Authority Letter
                  </Text>
                  <Text fz="xs" c="slate.6">
                    Download, sign, and upload the signed authority letter
                  </Text>
                </Box>
              </Flex>
              <Flex gap="xs" style={{ alignSelf: "flex-end" }}>
                {authorityLetterTemplateUrl ? (
                  <Button
                    component="a"
                    href={authorityLetterTemplateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    variant="filled"
                    color="primary"
                    leftSection={<IconDownload size={13} />}
                  >
                    Download
                  </Button>
                ) : (
                  <Button size="xs" variant="filled" color="primary" leftSection={<IconDownload size={13} />} disabled>
                    Download
                  </Button>
                )}
                <FileButton
                  onChange={(file) => {
                    if (file && file.size > 10 * 1024 * 1024) {
                      errorNotification({ title: "File too large", message: "Max 10MB allowed." });
                      return;
                    }
                    handleUpdateAuthorityLetter("file", file);
                  }}
                  accept=".pdf,.doc,.docx"
                  disabled={hasAffidavit}
                >
                  {(props) => (
                    <Button {...props} size="xs" variant="outline" leftSection={<IconUpload size={13} />}>
                      Upload
                    </Button>
                  )}
                </FileButton>
              </Flex>
            </Flex>
          </Paper>
          {authorityLetterData.file ? (
            <Flex
              align="center"
              justify="space-between"
              p="sm"
              style={{ border: "1px solid #E5E7EB", borderRadius: 8 }}
            >
              <Flex align="center" gap="xs" style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                <IconFileText size={18} color="#2563EB" style={{ flexShrink: 0 }} />
                <Text fz="sm" truncate>
                  {authorityLetterData.file.name}
                </Text>
              </Flex>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                style={{ flexShrink: 0 }}
                onClick={() => handleUpdateAuthorityLetter("file", null)}
              >
                Remove
              </Button>
            </Flex>
          ) : hasAffidavit ? (
            <Text fz="xs" c="slate.4">
              You've uploaded an Affidavit. Remove it to upload an Authority Letter instead.
            </Text>
          ) : null}
        </Box>

        <Flex align="center" my="md">
          <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
          <Text fz="xs" px="sm" c="slate.5">
            OR
          </Text>
          <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
        </Flex>

        <Box mb="sm">
          <Flex justify="space-between" align="flex-start" mb="xs">
            <Box>
              <Text fz="sm" fw={500}>
                Affidavit (Self-declaration, confirming ownership and authority to represent the business)
              </Text>
              <Text fz="xs" c="slate.6">
                For Partnership/Sole Proprietorship: download the template, sign it, and upload the signed document.
              </Text>
            </Box>
            <Badge color="red" variant="light" size="sm" radius="xl">
              MANDATORY
            </Badge>
          </Flex>
          <Paper withBorder shadow="none" p="sm" radius="md" mb="xs" style={{ borderColor: "#E5E7EB" }}>
            <Flex direction={{ base: "column", xs: "row" }} align={{ base: "flex-start", xs: "center" }} gap="sm">
              <Flex align="center" gap="sm" style={{ flex: 1 }}>
                <Box bg="gray.1" p="xs" style={{ borderRadius: "50%", display: "inline-flex", flexShrink: 0 }}>
                  <IconDownload size={16} color="var(--mantine-color-slate-6)" />
                </Box>
                <Box>
                  <Text fw={500} fz="sm">
                    Affidavit
                  </Text>
                  <Text fz="xs" c="slate.6">
                    Download, sign, and upload the signed affidavit
                  </Text>
                </Box>
              </Flex>
              <Flex gap="xs" style={{ alignSelf: "flex-end" }}>
                {affidavitTemplateUrl ? (
                  <Button
                    component="a"
                    href={affidavitTemplateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    variant="filled"
                    color="primary"
                    leftSection={<IconDownload size={13} />}
                  >
                    Download
                  </Button>
                ) : (
                  <Button size="xs" variant="filled" color="primary" leftSection={<IconDownload size={13} />} disabled>
                    Download
                  </Button>
                )}
                <FileButton
                  onChange={(file) => {
                    if (file && file.size > 10 * 1024 * 1024) {
                      errorNotification({ title: "File too large", message: "Max 10MB allowed." });
                      return;
                    }
                    handleUpdateMandatory("file", file);
                  }}
                  accept=".pdf,.doc,.docx"
                  disabled={hasAuthorityLetter}
                >
                  {(props) => (
                    <Button {...props} size="xs" variant="outline" leftSection={<IconUpload size={13} />}>
                      Upload
                    </Button>
                  )}
                </FileButton>
              </Flex>
            </Flex>
          </Paper>
          {mandatoryData.file ? (
            <Flex
              align="center"
              justify="space-between"
              p="sm"
              style={{ border: "1px solid #E5E7EB", borderRadius: 8 }}
            >
              <Flex align="center" gap="xs" style={{ minWidth: 0, flex: 1, marginRight: 8 }}>
                <IconFileText size={18} color="#2563EB" style={{ flexShrink: 0 }} />
                <Text fz="sm" truncate>
                  {mandatoryData.file.name}
                </Text>
              </Flex>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                style={{ flexShrink: 0 }}
                onClick={() => handleUpdateMandatory("file", null)}
              >
                Remove
              </Button>
            </Flex>
          ) : hasAuthorityLetter ? (
            <Text fz="xs" c="slate.4">
              You've uploaded an Authority Letter. Remove it to upload an Affidavit instead.
            </Text>
          ) : null}
        </Box>

        <Flex justify="flex-end" mt="xl">
          <Button
            size="md"
            px="xl"
            onClick={handleFinish}
            loading={loading}
            disabled={!hasRequiredDocument || loading}
          >
            Complete registration
          </Button>
        </Flex>
      </Paper>
    </SignupFlowLayout>
  );
}
